import { LessonPoll, POLL_MAX_OPTIONS, POLL_MIN_OPTIONS } from '../../../../../api/types/lesson';

export interface PollValidationErrors {
  question?: string;
  options?: string;
}

// A poll is optional: an untouched one is left off the lesson entirely so lessons can be
// saved in segments. Once any part of it is filled in, the whole poll has to be valid -
// a question without options (or the reverse) is never publishable.
export const isPollEmpty = ({ question, options }: { question: string; options: string[] }): boolean =>
  !question?.trim()?.length && !options.some((option) => option?.trim()?.length);

export const validatePoll = ({
  question,
  options,
}: {
  question: string;
  options: string[];
}): PollValidationErrors | undefined => {
  if (isPollEmpty({ question, options })) {
    return undefined;
  }

  const errors: PollValidationErrors = {};

  if (!question?.trim()?.length) {
    errors.question = 'Poll question is a required field';
  }

  const trimmedOptions = options.map((option) => option?.trim()).filter((option) => option?.length);
  if (trimmedOptions.length < POLL_MIN_OPTIONS) {
    errors.options = `Enter at least ${POLL_MIN_OPTIONS} non-empty options`;
  } else if (trimmedOptions.length > POLL_MAX_OPTIONS) {
    errors.options = `A poll can have at most ${POLL_MAX_OPTIONS} options`;
  } else if (new Set(trimmedOptions.map((option) => option.toLowerCase())).size !== trimmedOptions.length) {
    errors.options = 'Poll options must be unique';
  }

  return Object.keys(errors).length ? errors : undefined;
};

export const buildPollForSave = ({ question, options }: { question: string; options: string[] }): LessonPoll => ({
  question: question.trim(),
  options: options.map((option) => option.trim()).filter((option) => option.length),
});
